import { Icon } from '../common';
import { StayTable } from './StayTable';
import { PanelEmpty } from './PanelEmpty';
import { amountDue, dogsOf } from '../bookings/domain';
import { fmtEuro } from '../../lib/money';
import type { Booking } from '../bookings/types';

// Every stay in the block tonight (daily-command-strip AC17, AC18).
//
// ONE ORDER, ALWAYS: soonest departure first. The sort control offered By area
// and A–Z as well, which is three ways to read a list you open to answer one
// question — who is going home. It is not announced either; a list with one
// order does not need to say so.
export interface ResidencePanelProps {
  rows: Booking[];
  onOpenBooking: (bookingId: string) => void;
  onViewAll: () => void;
  onGoToArrivals?: () => void;
  // Any day other than today is a date-based PROJECTION, not a live headcount:
  // confirmed future stays appear regardless of arrival status. The copy said
  // "tonight" and talked about marking dogs arrived on both (PR #164, codex P2).
  live?: boolean;
}

export function ResidencePanel({ rows, onOpenBooking, onViewAll, onGoToArrivals, live = true }: ResidencePanelProps) {
  if (rows.length === 0) {
    return (
      <div className="cs-panel">
        <PanelEmpty
          icon="paw"
          line="The block is empty"
          detail={
            live
              ? 'Nobody is boarding tonight. Dogs appear here the moment they are marked arrived on the desk.'
              : 'No stay covers this day. Bookings appear here as soon as one spans it — they do not need to have arrived.'
          }
          action={onGoToArrivals ? { label: 'Go to drop-offs', onClick: onGoToArrivals } : undefined}
        />
      </div>
    );
  }
  const sorted = [...rows].sort((a, b) =>
    a.checkOut < b.checkOut ? -1 : a.checkOut > b.checkOut ? 1 : (a.dogs[0]?.name ?? '').localeCompare(b.dogs[0]?.name ?? ''),
  );
  const dogs = sorted.reduce((n, b) => n + dogsOf(b), 0);
  const due = sorted.reduce((n, b) => n + Math.max(0, amountDue(b)), 0);
  const owing = sorted.filter((b) => amountDue(b) > 0).length;
  return (
    <div className="cs-panel">
      <div className="cs-panel-head">
        <Icon name="paw" size={13} className="cs-panel-ico" />
        <span className="cs-panel-label">In residence</span>
        <span className="cs-panel-sum">
          {dogs} dogs · {sorted.length} stays
        </span>
        <button type="button" className="cs-panel-link" onClick={onViewAll}>
          Open in Bookings →
        </button>
      </div>
      <StayTable rows={sorted} onOpenBooking={onOpenBooking} label="Dogs in residence" />
      <div className="cs-panel-foot">
        <span>
          {owing} stay{owing === 1 ? '' : 's'} with money outstanding
        </span>
        <span className="cs-panel-tot">{due > 0 ? `${fmtEuro(due)} outstanding` : 'All settled'}</span>
      </div>
    </div>
  );
}
